import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import LoadingOverlay from '../components/LoadingOverlay';
import SinAccesoResidente from '../components/SinAccesoResidente';
import { usePersonaResidente } from '../components/PersonaResidenteContext';
import { useTerminologia } from '../hooks/useTerminologia';

const HOST_API = process.env.REACT_APP_URL;

const FORMAS_PAGO = [
    { label: 'Efectivo',          value: 1 },
    { label: 'Transferencia',     value: 2 },
    { label: 'Cheque',            value: 3 },
    { label: 'Débito automático', value: 4 },
];

const CONCEPTOS = [
    { label: 'Expensa',      value: 1 },
    { label: 'Plan de pago', value: 2 },
];

const FORM_EMPTY = {
    unidadFuncionalId: null,
    periodo:           null,
    fechaPago:         null,
    importe:           null,
    formaPagoId:       null,
    conceptoPagoId:    1,
    planPagoCuotaId:   null,
    detalle:           '',
};

const ESTADO_CUOTA_COLORES = {
    Pendiente: { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
    Vencida:   { bg: '#fef3f2', color: '#d92d20', border: '#fda29b' },
    Pagada:    { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
    Parcial:   { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
};

const cuotaEstadoBadge = (estado) => {
    const s = ESTADO_CUOTA_COLORES[estado] || { bg: '#f2f4f7', color: '#475467', border: '#d0d5dd' };
    return (
        <span style={{
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            textTransform: 'capitalize',
        }}>
            {estado?.toLowerCase()}
        </span>
    );
};

const fmtFecha   = (v) => v ? new Date(v).toLocaleDateString('es-AR') : '—';
const fmtImporte = (v) => v != null ? `$ ${Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : '—';
const fmtPeriodo = (v) => v ? new Date(v).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' }) : '—';
const fmtSize    = (b) => b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;

const SubirComprobante = () => {
    const toast       = useRef(null);
    const fileInputRef = useRef(null);
    const { barrio }  = useContext(BarrioContext);
    const { sinAcceso } = usePersonaResidente();
    const t = useTerminologia();

    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');
    const usuarioId = JSON.parse(localStorage.getItem('user') || '{}').id
        || JSON.parse(localStorage.getItem('user') || '{}').usuarioId
        || 0;

    // ─── Data ────────────────────────────────────────────────────────────────────
    const [ufs,       setUfs]       = useState([]);
    const [personaId, setPersonaId] = useState(null);
    // 'cargando' | 'listo' | 'error' -- evita avisar "sin UF" antes de que respondan las UFs del usuario
    const [ufsEstado, setUfsEstado] = useState('cargando');
    const [pagos,     setPagos]     = useState([]);
    const [loading,   setLoading]   = useState(false);

    // ─── Filtros ─────────────────────────────────────────────────────────────────
    const [filtroUF,      setFiltroUF]      = useState(null);
    const [filtroPeriodo, setFiltroPeriodo] = useState(null);

    // ─── Modal ───────────────────────────────────────────────────────────────────
    const [modalVisible, setModalVisible] = useState(false);
    const [form,         setForm]         = useState({ ...FORM_EMPTY });
    const [archivo,      setArchivo]      = useState(null);
    const [dragOver,     setDragOver]     = useState(false);
    const [submitting,   setSubmitting]   = useState(false);

    // ─── Expensa: vencimientos (GetInformarPagosBarrio) ───────────────────────────
    const [infoExpensa,        setInfoExpensa]        = useState(null);
    const [loadingInfoExpensa, setLoadingInfoExpensa]  = useState(false);
    const [ventanaPago,        setVentanaPago]        = useState(null); // 'v1' | 'v2' | 'otros'

    // ─── Plan de pago: planes y cuotas ─────────────────────────────────────────────
    const [planes,        setPlanes]        = useState([]);
    const [loadingPlanes, setLoadingPlanes]  = useState(false);
    const [planPagoId,    setPlanPagoId]     = useState(null);
    const [cuotas,        setCuotas]         = useState([]);
    const [loadingCuotas, setLoadingCuotas]  = useState(false);

    // ─── Carga UFs del usuario ────────────────────────────────────────────────────
    useEffect(() => {
        if (!barrio?.id || !usuarioId) return;
        setUfsEstado('cargando');
        setPersonaId(null);
        axios.get(`${HOST_API}api/unidadesFuncionales/GetUFsByUsuario?usuarioId=${usuarioId}&barrioId=${barrio.id}`)
            .then(({ data }) => {
                setUfs(data);
                // Guardar personaId que devuelve el SP
                if (data.length > 0 && data[0].personaId) {
                    setPersonaId(data[0].personaId);
                } else if (data.length > 0) {
                    // Tiene UFs pero sin personaId: la búsqueda va a quedar bloqueada
                    // en silencio si esto no se avisa acá.
                    console.warn('[SubirComprobante] GetUFsByUsuario devolvió UFs sin personaId', data);
                }
                if (data.length === 1) {
                    setFiltroUF(data[0].unidadFuncionalId);
                    setForm(f => ({ ...f, unidadFuncionalId: data[0].unidadFuncionalId }));
                }
                setUfsEstado('listo');
            })
            .catch(() => setUfsEstado('error'));
    }, [barrio?.id, usuarioId]);

    // ─── Carga info de vencimientos de la expensa (Concepto = Expensa) ───────────
    useEffect(() => {
        setInfoExpensa(null);
        setVentanaPago(null);
        setForm(f => ({ ...f, importe: null }));
        if (!modalVisible || form.conceptoPagoId !== 1 || !form.unidadFuncionalId || !form.periodo || !barrio?.id) return;
        const ufObj = ufs.find(u => u.unidadFuncionalId === form.unidadFuncionalId);
        if (!ufObj) return;
        setLoadingInfoExpensa(true);
        axios.get(`${HOST_API}api/pagosUF/GetInformarPagosBarrio`, {
            params: {
                unidadFuncionalId: form.unidadFuncionalId,
                barrioId:          barrio.id,
                administracionId,
                conceptoPagoId:    1,
                periodo:           form.periodo.toISOString(),
            },
        })
            .then(({ data }) => setInfoExpensa(data))
            .catch(() => setInfoExpensa(null))
            .finally(() => setLoadingInfoExpensa(false));
    }, [modalVisible, form.conceptoPagoId, form.unidadFuncionalId, form.periodo, barrio?.id, ufs]);

    const seleccionarVencimiento = (tipo) => {
        setVentanaPago(tipo);
        if (tipo === 'v1') {
            setForm(f => ({ ...f, importe: infoExpensa?.importePrimerVencimiento ?? null }));
        } else if (tipo === 'v2') {
            setForm(f => ({ ...f, importe: infoExpensa?.importeSegundoVencimiento ?? null }));
        } else {
            setForm(f => ({ ...f, importe: null }));
        }
    };

    // ─── Carga planes de pago de la UF seleccionada (Concepto = Plan de pago) ────
    useEffect(() => {
        setPlanPagoId(null);
        setCuotas([]);
        setForm(f => ({ ...f, planPagoCuotaId: null }));
        if (!modalVisible || form.conceptoPagoId !== 2 || !form.unidadFuncionalId) {
            setPlanes([]);
            return;
        }
        setLoadingPlanes(true);
        axios.get(`${HOST_API}api/PlanPago/GetPlanesPago`, {
            params: { administracionId, unidadFuncionalId: form.unidadFuncionalId },
        })
            .then(({ data }) => setPlanes((data || []).filter(p => p.saldo > 0)))
            .catch(() => setPlanes([]))
            .finally(() => setLoadingPlanes(false));
    }, [modalVisible, form.conceptoPagoId, form.unidadFuncionalId]);

    useEffect(() => {
        if (planes.length === 1) setPlanPagoId(planes[0].planPagoId);
    }, [planes]);

    // ─── Carga cuotas del plan de pago seleccionado ────────────────────────────────
    useEffect(() => {
        if (!planPagoId) { setCuotas([]); return; }
        setLoadingCuotas(true);
        axios.get(`${HOST_API}api/PlanPago/GetPlanPagoCuotas`, {
            params: { administracionId, planPagoId },
        })
            .then(({ data }) => setCuotas((data || []).filter(c => c.saldo > 0)))
            .catch(() => setCuotas([]))
            .finally(() => setLoadingCuotas(false));
    }, [planPagoId]);

    const planLabel = (p) =>
        `${p.concepto} · ${p.cantidadCuotas} cuotas · saldo ${fmtImporte(p.saldo)} · ${p.estadoPlanPago}`;

    const seleccionarPlan = (id) => {
        setPlanPagoId(id);
        setForm(f => ({ ...f, planPagoCuotaId: null }));
    };

    const seleccionarCuota = (planPagoCuotaId) => {
        const cuota = cuotas.find(c => c.planPagoCuotaId === planPagoCuotaId);
        setForm(f => ({
            ...f,
            planPagoCuotaId,
            importe: cuota ? cuota.saldo : f.importe,
            periodo: cuota ? new Date(cuota.periodo) : f.periodo,
        }));
    };

    // ─── Carga pagos (filtrados por personaId del usuario) ───────────────────────
    const fetchPagos = useCallback(async () => {
        if (!barrio?.id || !personaId) {
            // Mientras GetUFsByUsuario no respondió, personaId todavía es null: no es un
            // error, solo hay que esperar. Se avisa recién cuando la respuesta llegó y
            // de verdad no trajo una persona vinculada (o cuando la consulta falló).
            if (ufsEstado === 'listo' && !personaId) {
                console.warn('[SubirComprobante] el usuario no tiene persona/UF vinculada en este barrio', { barrioId: barrio?.id, usuarioId });
                toast.current?.show({
                    severity: 'warn',
                    summary: 'No se pudo identificar al usuario',
                    detail: `No encontramos ${t.unUnidadFuncional} vinculada a tu usuario en este ${t.barrio}. Contactá al administrador.`,
                    life: 5000,
                });
            } else if (ufsEstado === 'error') {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: `No se pudieron cargar tus ${t.unidadesFuncionales}. Intentá de nuevo en unos minutos.`,
                    life: 5000,
                });
            }
            return;
        }
        setLoading(true);
        try {
            let url = `${HOST_API}api/pagosUF/GetPagos?administracionId=${administracionId}&personaId=${personaId}`;
            if (filtroUF)      url += `&unidadFuncionalId=${filtroUF}`;
            if (filtroPeriodo) url += `&periodo=${filtroPeriodo.toISOString()}`;
            const { data } = await axios.get(url);
            setPagos(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los pagos', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, personaId, filtroUF, filtroPeriodo, ufsEstado]);

    useEffect(() => { fetchPagos(); }, [fetchPagos]);

    // ─── Archivo ─────────────────────────────────────────────────────────────────
    const setFile = (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.current.show({ severity: 'warn', summary: 'Archivo muy grande', detail: 'El archivo no puede superar 10 MB', life: 3500 });
            return;
        }
        setArchivo(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        setFile(e.dataTransfer.files[0]);
    };

    const handleRemoveFile = () => {
        setArchivo(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ─── Registrar pago ───────────────────────────────────────────────────────────
    const guardarPago = async () => {
        if (!form.unidadFuncionalId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione la unidad funcional', life: 3000 }); return; }
        if (!form.periodo)           { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el período', life: 3000 }); return; }
        if (!form.fechaPago)         { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese la fecha de pago', life: 3000 }); return; }
        if (form.conceptoPagoId === 1 && !ventanaPago) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el vencimiento a abonar', life: 3000 }); return; }
        if (form.conceptoPagoId === 2 && !form.planPagoCuotaId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione la cuota a abonar', life: 3000 }); return; }
        if (!form.importe)           { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el importe', life: 3000 }); return; }
        if (!form.formaPagoId)       { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione la forma de pago', life: 3000 }); return; }

        setSubmitting(true);
        try {
            const modelo = {
                unidadFuncionalId: form.unidadFuncionalId,
                barrioId:          barrio.id,
                administracionId,
                fechaPago:         form.fechaPago,
                importe:           form.importe,
                formaPagoId:       form.formaPagoId,
                detalle:           form.detalle || null,
                usuarioId,
                periodo:           form.periodo,
                conceptoPagoId:    form.conceptoPagoId,
                planPagoCuotaId:   form.planPagoCuotaId || null,
                esImportado:       false,
            };

            const fd = new FormData();
            fd.append('model', JSON.stringify(modelo));
            if (archivo) fd.append('file', archivo);

            await axios.post(`${HOST_API}api/pagosUF/RegistrarPago`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            toast.current.show({
                severity: 'success',
                summary:  'Pago registrado',
                detail:   archivo ? 'Pago y comprobante enviados correctamente' : 'Pago registrado correctamente',
                life:     3500,
            });

            setModalVisible(false);
            setForm({ ...FORM_EMPTY, unidadFuncionalId: ufs.length === 1 ? ufs[0].unidadFuncionalId : null });
            setArchivo(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            fetchPagos();
        } catch (err) {
            toast.current.show({
                severity: 'error',
                summary:  'Error',
                detail:   err?.response?.data?.errorDisplay || 'No se pudo registrar el pago',
                life:     4000,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const abrirModal = () => {
        setForm({
            ...FORM_EMPTY,
            unidadFuncionalId: ufs.length === 1 ? ufs[0].unidadFuncionalId : null,
        });
        setArchivo(null);
        setModalVisible(true);
    };

    // ─── Columnas de la grilla ────────────────────────────────────────────────────
    const ESTADO_PAGO_COLORES = {
        Pendiente: { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
        Aprobado:  { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
        Rechazado: { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
    };
    const estadoBody = (row) => {
        const s = ESTADO_PAGO_COLORES[row.estado] || { bg: '#f2f4f7', color: '#475467', border: '#d0d5dd' };
        return (
            <span style={{
                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {row.estado}
            </span>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────────
    if (sinAcceso) return <SinAccesoResidente />;

    return (
        <>
            <Toast ref={toast} />

<LoadingOverlay visible={loading} />

            <h4>Mis Pagos</h4>
            <br />

            {/* ─── Filtros + acción ────────────────────────────────────────────── */}
            <div className="flex justify-content-between align-items-end mb-3 flex-wrap gap-2">
                <div className="flex gap-2 flex-wrap align-items-end">
                    {ufs.length > 1 && (
                        <div>
                            <label style={lbl}>{t.UnidadFuncional}</label>
                            <Dropdown
                                value={filtroUF}
                                options={ufs}
                                optionLabel="unidadFuncional"
                                optionValue="unidadFuncionalId"
                                onChange={(e) => setFiltroUF(e.value)}
                                placeholder="Todas mis UFs"
                                showClear
                                className="p-inputtext-sm"
                                style={{ width: 180 }}
                            />
                        </div>
                    )}
                    <div>
                        <label style={lbl}>Período</label>
                        <Calendar
                            value={filtroPeriodo}
                            onChange={(e) => setFiltroPeriodo(e.value)}
                            view="month" dateFormat="mm/yy"
                            placeholder="Todos" showButtonBar
                            className="p-inputtext-sm"
                            style={{ width: 140 }}
                        />
                    </div>
                    <Button label="Buscar" icon="pi pi-search" onClick={fetchPagos} className="p-button-sm" />
                </div>
                <Button
                    label="Registrar Pago"
                    icon="pi pi-plus"
                    className="p-button-sm"
                    onClick={abrirModal}
                    disabled={ufs.length === 0}
                />
            </div>

            {/* ─── Grilla (solo lectura) ────────────────────────────────────────── */}
            <div style={{ overflowX: 'auto' }}>
                <DataTable
                    value={pagos}
                    paginator rows={10} rowsPerPageOptions={[5, 10, 25]}
                    showGridlines responsiveLayout="scroll"
                    emptyMessage="No hay pagos registrados"
                    className="p-datatable-gridlines bg-white"
                    sortField="fechaPago" sortOrder={-1}
                >
                    <Column field="unidadFuncional" header={t.UF}          sortable style={{ width: '5rem', textAlign: 'center' }} />
                    <Column field="periodo"         header="Período"      sortable body={(r) => fmtPeriodo(r.periodo)}  style={{ width: '10rem' }} />
                    <Column field="fechaPago"       header="Fecha pago"   sortable body={(r) => fmtFecha(r.fechaPago)}  style={{ width: '9rem' }} />
                    <Column field="importe"         header="Importe"      sortable body={(r) => fmtImporte(r.importe)}  style={{ width: '10rem', textAlign: 'right' }} />
                    <Column field="formaPago"       header="Forma pago"   sortable style={{ width: '10rem' }} />
                    <Column field="detalle"         header="Detalle"      body={(r) => r.detalle || '—'} />
                    <Column header="Estado" body={estadoBody} style={{ width: '8rem' }} />
                </DataTable>
            </div>

            {/* ─── Modal: Registrar Pago ────────────────────────────────────────── */}
            <Dialog
                header="Registrar Pago"
                visible={modalVisible}
                style={{ width: '500px' }}
                breakpoints={{ '768px': '95vw' }}
                modal
                onHide={() => setModalVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalVisible(false)} disabled={submitting} />
                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={guardarPago} loading={submitting} />
                    </div>
                }
            >
                <div className="grid p-fluid mt-1">
                    {/* UF — combo si tiene más de una, texto fijo si tiene una sola */}
                    {ufs.length > 1 ? (
                        <div className="col-12">
                            <label style={lbl}>{t.UnidadFuncional} *</label>
                            <Dropdown
                                value={form.unidadFuncionalId}
                                options={ufs}
                                optionLabel="unidadFuncional"
                                optionValue="unidadFuncionalId"
                                onChange={(e) => setForm(f => ({ ...f, unidadFuncionalId: e.value }))}
                                placeholder="Seleccioná la UF a abonar"
                                className="p-inputtext-sm"
                            />
                        </div>
                    ) : ufs.length === 1 && (
                        <div className="col-12">
                            <label style={lbl}>{t.UnidadFuncional}</label>
                            <p style={{ margin: 0, fontWeight: 600, color: '#101828', fontSize: '0.9rem' }}>
                                {ufs[0].unidadFuncional}
                            </p>
                        </div>
                    )}

                    <div className="col-6">
                        <label style={lbl}>Período *</label>
                        <Calendar
                            value={form.periodo}
                            onChange={(e) => setForm(f => ({ ...f, periodo: e.value }))}
                            view="month" dateFormat="mm/yy"
                            placeholder="mm/aaaa"
                            className="p-inputtext-sm"
                        />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Fecha de pago *</label>
                        <Calendar
                            value={form.fechaPago}
                            onChange={(e) => setForm(f => ({ ...f, fechaPago: e.value }))}
                            dateFormat="dd/mm/yy" showIcon
                            placeholder="dd/mm/aaaa"
                            className="p-inputtext-sm"
                        />
                    </div>

                    <div className="col-12">
                        <label style={lbl}>Concepto</label>
                        <Dropdown
                            value={form.conceptoPagoId}
                            options={CONCEPTOS}
                            onChange={(e) => setForm(f => ({ ...f, conceptoPagoId: e.value }))}
                            className="p-inputtext-sm"
                        />
                    </div>

                    {/* ─── Expensa: vencimiento 1 / 2 / otros ─────────────────────── */}
                    {form.conceptoPagoId === 1 && (
                        <div className="col-12">
                            <label style={lbl}>Vencimiento a abonar *</label>
                            {loadingInfoExpensa ? (
                                <div className="flex align-items-center gap-2" style={{ color: '#667085', fontSize: '0.85rem' }}>
                                    <i className="pi pi-spin pi-spinner" /> Buscando información de la expensa...
                                </div>
                            ) : (
                                <div className="flex flex-column gap-2">
                                    <div className="flex align-items-center gap-2">
                                        <RadioButton
                                            inputId="venc1"
                                            name="vencimiento"
                                            value="v1"
                                            checked={ventanaPago === 'v1'}
                                            disabled={!infoExpensa}
                                            onChange={() => seleccionarVencimiento('v1')}
                                        />
                                        <label htmlFor="venc1" style={{ fontSize: '0.87rem' }}>
                                            Vencimiento 1{infoExpensa ? ` · ${fmtImporte(infoExpensa.importePrimerVencimiento)}` : ''}
                                        </label>
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <RadioButton
                                            inputId="venc2"
                                            name="vencimiento"
                                            value="v2"
                                            checked={ventanaPago === 'v2'}
                                            disabled={!infoExpensa}
                                            onChange={() => seleccionarVencimiento('v2')}
                                        />
                                        <label htmlFor="venc2" style={{ fontSize: '0.87rem' }}>
                                            Vencimiento 2{infoExpensa ? ` · ${fmtImporte(infoExpensa.importeSegundoVencimiento)}` : ''}
                                        </label>
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <RadioButton
                                            inputId="vencOtros"
                                            name="vencimiento"
                                            value="otros"
                                            checked={ventanaPago === 'otros'}
                                            onChange={() => seleccionarVencimiento('otros')}
                                        />
                                        <label htmlFor="vencOtros" style={{ fontSize: '0.87rem' }}>Otros</label>
                                    </div>
                                </div>
                            )}
                            {ventanaPago === 'otros' && (
                                <InputNumber
                                    value={form.importe}
                                    onValueChange={(e) => setForm(f => ({ ...f, importe: e.value }))}
                                    mode="currency" currency="ARS" locale="es-AR"
                                    placeholder="Importe"
                                    className="p-inputtext-sm mt-2"
                                />
                            )}
                        </div>
                    )}

                    {/* ─── Plan de pago: cuotas pendientes ────────────────────────── */}
                    {form.conceptoPagoId === 2 && (
                        <div className="col-12">
                            {planes.length > 1 && (
                                <>
                                    <label style={lbl}>Plan de pago *</label>
                                    <Dropdown
                                        value={planPagoId}
                                        options={planes}
                                        optionLabel="planPagoId"
                                        optionValue="planPagoId"
                                        itemTemplate={planLabel}
                                        valueTemplate={(o) => o ? planLabel(o) : 'Seleccionar plan'}
                                        onChange={(e) => seleccionarPlan(e.value)}
                                        loading={loadingPlanes}
                                        placeholder="Seleccionar plan"
                                        className="p-inputtext-sm mb-2"
                                    />
                                </>
                            )}
                            <label style={lbl}>Cuotas a abonar *</label>
                            {loadingPlanes || loadingCuotas ? (
                                <div className="flex align-items-center gap-2" style={{ color: '#667085', fontSize: '0.85rem' }}>
                                    <i className="pi pi-spin pi-spinner" /> Buscando cuotas del plan de pago...
                                </div>
                            ) : cuotas.length === 0 ? (
                                <p style={{ fontSize: '0.85rem', color: '#98a2b3', margin: 0 }}>
                                    {!form.unidadFuncionalId
                                        ? 'Seleccioná la unidad funcional'
                                        : planes.length === 0
                                            ? 'Esta UF no tiene planes de pago con saldo'
                                            : 'Este plan no tiene cuotas pendientes'}
                                </p>
                            ) : (
                                <div className="flex flex-column gap-2">
                                    {cuotas.map((c) => (
                                        <div
                                            key={c.planPagoCuotaId}
                                            className="flex align-items-center justify-content-between"
                                            style={{ border: '1px solid #e4e7ec', borderRadius: 8, padding: '0.5rem 0.75rem' }}
                                        >
                                            <div className="flex align-items-center gap-2">
                                                <RadioButton
                                                    inputId={`cuota-${c.planPagoCuotaId}`}
                                                    name="cuota"
                                                    value={c.planPagoCuotaId}
                                                    checked={form.planPagoCuotaId === c.planPagoCuotaId}
                                                    onChange={() => seleccionarCuota(c.planPagoCuotaId)}
                                                />
                                                <label htmlFor={`cuota-${c.planPagoCuotaId}`} style={{ fontSize: '0.85rem' }}>
                                                    Cuota {c.numeroCuota}/{c.cantidadCuotas} · {fmtPeriodo(c.periodo)} · {fmtImporte(c.saldo)}
                                                </label>
                                            </div>
                                            {cuotaEstadoBadge(c.estadoCuota)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="col-6">
                        <label style={lbl}>Forma de pago *</label>
                        <Dropdown
                            value={form.formaPagoId}
                            options={FORMAS_PAGO}
                            onChange={(e) => setForm(f => ({ ...f, formaPagoId: e.value }))}
                            placeholder="Seleccionar"
                            className="p-inputtext-sm"
                        />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Detalle</label>
                        <InputText
                            value={form.detalle}
                            onChange={(e) => setForm(f => ({ ...f, detalle: e.target.value }))}
                            placeholder="Nro. transferencia, etc."
                            className="p-inputtext-sm"
                        />
                    </div>

                    {/* Comprobante adjunto (opcional) */}
                    <div className="col-12">
                        <label style={lbl}>
                            Comprobante&nbsp;
                            <span style={{ fontWeight: 400, color: '#98a2b3' }}>(opcional)</span>
                        </label>

                        {!archivo ? (
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={handleDrop}
                                style={{
                                    border:       `2px dashed ${dragOver ? '#1570ef' : '#d0d5dd'}`,
                                    borderRadius: 10,
                                    padding:      '1.25rem 1rem',
                                    textAlign:    'center',
                                    cursor:       'pointer',
                                    background:   dragOver ? '#f0f6ff' : '#fafafa',
                                    transition:   'all 0.15s',
                                }}
                            >
                                <i className="pi pi-cloud-upload" style={{ fontSize: '1.6rem', color: dragOver ? '#1570ef' : '#98a2b3', marginBottom: '0.4rem', display: 'block' }} />
                                <p style={{ margin: 0, fontWeight: 500, color: '#344054', fontSize: '0.85rem' }}>
                                    Arrastrá o <span style={{ color: '#1570ef' }}>seleccioná</span> un archivo
                                </p>
                                <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: '#98a2b3' }}>
                                    PDF, JPG, PNG — máx. 10 MB
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '0.75rem',
                                border: '1px solid #d1fadf', borderRadius: 8,
                                padding: '0.7rem 1rem', background: '#f6fef9',
                            }}>
                                <i className="pi pi-file" style={{ fontSize: '1.3rem', color: '#12b76a' }} />
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                    <p style={{ margin: 0, fontWeight: 600, color: '#101828', fontSize: '0.83rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {archivo.name}
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#667085' }}>{fmtSize(archivo.size)}</p>
                                </div>
                                <button type="button" onClick={handleRemoveFile}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#98a2b3' }}>
                                    <i className="pi pi-times" />
                                </button>
                            </div>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            style={{ display: 'none' }}
                            onChange={(e) => setFile(e.target.files[0])}
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
};

const lbl = {
    display:       'block',
    marginBottom:  '0.35rem',
    fontWeight:    500,
    color:         '#344054',
    fontSize:      '0.87rem',
};

export default SubirComprobante;
